import React, { Fragment, useEffect, useState } from 'react';

import HorizontalAdvertisement from '@components/FeaturedPartnerAd/Horizontal';
import ArticleFeedItem from '@components/FeedItems/ArticleFeedItem';
import TITVStreamFeedItem from '@components/FeedItems/TITVStreamFeedItem';
import TopArticlesSection, { Page } from '@components/FeedItems/TopArticlesSection';
import AIPromo from '@components/pages/Home/Units/AIPromo';
import FeaturedNewslettersUnit from '@components/pages/Home/Units/FeaturedNewslettersUnit';
import useEventTracking from '@hooks/useEventTracking';
import useSearchParams from '@hooks/useSearchParams';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { FeedContentType } from '@utils/enums';

import {
  Advertisement,
  Article,
  Briefing,
  FeedUnit,
  FeedUnits,
  TITVStream,
  UnitName,
} from '../../interfaces/homepage';
import { finance_path, root_path, technology_path } from '../../routes';
import { SecondaryButton } from '../Button/variants';
import BriefingFeedItem from '../FeedItems/BriefingFeedItem';
import Tabs from '../Tabs';
import ForumUnit from '../pages/Home/Units/ForumUnit';
import NewsletterUnit from '../pages/Home/Units/NewsletterUnit';

const PAGE = 1;
const PAGE_SIZE = 25;
const RECENT = 'recent';
const POPULAR = 'popular';

interface MainFeedProps {
  featuredArticles?: Article[];
  feedContent: Article[] | (Article | Advertisement | Briefing | TITVStream)[];
  feedUnits?: FeedUnits;
  highlightType?: string;
  includeTabs?: boolean;
  page?: number;
  pageSize?: number;
  view: string;
}

const MainFeed = ({
  featuredArticles,
  feedContent,
  feedUnits,
  highlightType,
  includeTabs = true,
  page = PAGE,
  pageSize = PAGE_SIZE,
  view,
}: MainFeedProps) => {
  const [numVisibleRecords, setNumVisibleRecords] = useState(pageSize);
  const [currentPage, setCurrentPage] = useState(page ? Number(page) : 1);
  const { Track, trackEvent } = useEventTracking({ location: TrackingLocation.MainRiver });
  const [shouldIncludeTITVStreams, setShouldIncludeTITVStreams] = useState(false);
  const { paramValues } = useSearchParams(['titv']);
  const hydrated = useServerSideRendering();

  useEffect(() => {
    if (hydrated) {
      setShouldIncludeTITVStreams(paramValues['titv'] === 'true');
    }
  }, [hydrated, paramValues]);

  const startingRecordIndex = page && page > 1 ? pageSize * (page - 1) : 0;
  const endingRecordIndex = startingRecordIndex + numVisibleRecords;

  const nextPageParams = { page: currentPage + 1, view };
  const nextPagePath =
    highlightType === 'technology'
      ? technology_path(nextPageParams)
      : highlightType === 'finance'
      ? finance_path(nextPageParams)
      : root_path(nextPageParams);

  const handleReadMore = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setNumVisibleRecords((prevNumVisibleRecords) => prevNumVisibleRecords + pageSize);
    setCurrentPage((prev) => prev + 1);
    trackEvent(click({ clickTarget: ClickTarget.ReadMore }));
  };

  const renderFeedUnit = (feedUnit: FeedUnit) => {
    switch (feedUnit.unitName) {
      case UnitName.AIPromo:
        return <AIPromo />;
      case UnitName.FeaturedNewsletters:
        return <FeaturedNewslettersUnit feedUnit={feedUnit} />;
      case UnitName.Forum:
        return <ForumUnit feedUnit={feedUnit} />;
      case UnitName.TheWeekend:
        return <NewsletterUnit feedUnit={feedUnit} isTheWeekend />;
      default:
        return <NewsletterUnit feedUnit={feedUnit} />;
    }
  };

  return (
    <Track>
      <section>
        {includeTabs && (
          <Tabs
            tabs={[
              {
                to: RECENT,
                text: 'Latest',
              },
              {
                to: POPULAR,
                text: 'Most popular',
              },
            ]}
            view={view}
          />
        )}
        {featuredArticles?.length > 0 && (!page || page === 1) && (
          <TopArticlesSection
            page={Page.Home}
            sideArticles={featuredArticles.slice(1)}
            topArticle={featuredArticles[0]}
          />
        )}
        {feedContent.slice(startingRecordIndex, endingRecordIndex).map((item, index) => (
          <Fragment key={`${item.contentType}-${item.id}`}>
            {feedUnits?.[index] && renderFeedUnit(feedUnits[index])}
            {item.contentType === FeedContentType.Article && (
              <ArticleFeedItem
                authors={item.authors}
                commentsCount={item.commentsCount}
                contentIndex={index}
                highlight={item.highlight}
                id={item.id}
                isPro={item.isPro}
                newsletterSlug={item.newsletterSlug}
                picture={item.picture}
                publishedAt={item.publishedAt}
                skipPictureOptimization={item.skipPictureOptimization}
                slug={item.slug}
                title={item.title}
              />
            )}
            {item.contentType === FeedContentType.TITVStream && shouldIncludeTITVStreams && (
              <TITVStreamFeedItem titvStream={item} />
            )}
            {item.contentType === FeedContentType.Briefing && <BriefingFeedItem briefing={item} />}
            {item.contentType === FeedContentType.Advertisement && (
              <HorizontalAdvertisement
                advertisement={item}
                trackingLocation={TrackingLocation.MainRiver}
              />
            )}
          </Fragment>
        ))}
        {feedContent.length > endingRecordIndex && (
          <div className="px-16 pt-32 sm:px-0 sm:pt-48">
            <SecondaryButton
              className="bg-transparent focus:bg-transparent focus-visible:ring-0 max-sm:hover:!bg-transparent"
              as="link"
              onClickCapture={handleReadMore}
              fullWidth
              href={nextPagePath}
              data-testid="read-more-button"
            >
              Read more
            </SecondaryButton>
          </div>
        )}
      </section>
    </Track>
  );
};

export default MainFeed;
